import api from '../api';
import { languages } from '../../configs';
import { queueBundledKeysMissingFromDb } from './trans';

/**
 * Loads the DB-backed translation dictionary at app bootstrap and populates
 * the shared `languages` config object, keeping the exact same
 * `languages.i18n[lang][key]` shape that `trans()` already consumes.
 *
 * Language catalog (native names, sort) comes from /api/global/config
 * `languageList` (group-scoped) — applied via `applyLanguagesMeta`.
 *
 * Layout stays LTR for all languages (RTL flip was intentionally removed).
 *
 * Runtime dict = bundled fallback merged with DB (DB wins). On dev / doruk.tech,
 * bundled keys missing from DB are queued for upsert via translations-missing.
 */

const getActiveLang = () => document.documentElement.lang || 'tr';

const applyDictionary = (lang, dbDict) => {
    const bundled = languages.bundledI18n?.[lang] || {};
    const fromDb = dbDict && typeof dbDict === 'object' ? dbDict : {};
    // Keep local fallbacks visible for new keys; DB overrides on conflict.
    languages.i18n[lang] = { ...bundled, ...fromDb };
};

export const applyLanguagesMeta = (list) => {
    if (!Array.isArray(list)) return;
    const meta = {};
    list.forEach((item) => {
        if (!item || !item.Lang) return;
        meta[item.Lang] = {
            name: item.Name,
            nativeName: item.NativeName,
            sortOrder: item.SortOrder,
        };
    });
    languages.meta = meta;
};

const loadDictionary = async (lang) => {
    try {
        const res = await api.config.getTranslations();
        if (res?.success && res.data) {
            // Only diff against a real DB payload — never dump the whole
            // bundled file when the dictionary request fails.
            queueBundledKeysMissingFromDb(lang, res.data);
            applyDictionary(lang, res.data);
        }
    } catch (e) {
        // Network failure — keep the bundled dictionary as fallback.
    }
};

const bootstrapI18n = async (languageList) => {
    const lang = getActiveLang();
    if (languageList) {
        applyLanguagesMeta(languageList);
    }
    await loadDictionary(lang);
};

export default bootstrapI18n;
